import React from 'react';
import classNames from 'classnames';
import { useId } from '@floating-ui/react-dom-interactions';

import { RCNonModalDialogBase } from '../internalComponents/RCNonModalDialogBase';

type FloatingUIPositions = 'bottom' | 'bottom-start' | 'bottom-end';

interface RCPopoverProps {
  /** The open state of the component (controlled by the parent). */
  isOpen: boolean;

  /**
   * - *On Desktop:* fired when the user clicks the dismissal button or presses escape
   * - *On Mobile Web:* fired when the user taps outside the component.
   */
  onDismiss: Function;

  /** *Desktop Only* - A formatted block of content that renders inside of the popover between the header and dismiss button*/
  children: React.ReactNode;

  /** The element to which the popover will point & anchor. */
  anchor: JSX.Element;

  /** The popover automatically positions itself,
   * but if you need to ensure it is always flush left or right (e.g. to avoid covering up key UI, use this. */
  position?: FloatingUIPositions;

  /** Decreases the opacity a bit. Use this when the contents inside the popover have some sort of async process
   * that occurs while the popover is still open. */
  loading?: boolean;

  /** "Trapping" focus keeps the focus tab order locked to contents inside the Popover.
   * When `true`, a user is required to dismiss the popover before they can tab to other elements on the page.
   * Change to `false` any time the user doesn't have a way to dismiss the popover with a keyboard.
   */
  trapFocus?: boolean;
}

/**
 * Our general Popover component. Auto-positioning and accessibility-enhanced. Use this for submenus, dropdowns, and the like.
 */
export function RCPopover({
  isOpen = false,
  onDismiss,
  position = 'bottom-end',
  children,
  anchor,
  loading = false,
  trapFocus = true,
}: RCPopoverProps) {
  const id = useId();

  return (
    <RCNonModalDialogBase
      id={id}
      isOpen={isOpen}
      onOpenChange={() => onDismiss()}
      position={position}
      anchor={anchor}
      trapFocus={trapFocus}
    >
      {isOpen &&
        <div className={classNames(
          'rc-popover',
          { 'rc-popover--loading': loading },
        )}>
          {children}
        </div>
      }
    </RCNonModalDialogBase>
  );
}
